import { apiInternals, send } from './client';

/**
 * The device-replacement workflow (M-13, Task 5) — `GET/POST /replacements`
 * and the `dispense`/`reject` transition routes. Every shape here is
 * verified field-for-field against `App\Http\Resources\Replacements\
 * ReplacementResource` and `App\Support\OpenApi\ApiSpecification`'s own
 * `Replacement` schema in the API repository, not inferred from the task
 * brief's prose — `escalations.ts`'s and `justifications.ts`'s own docblocks
 * state the same discipline, and this one repeats it.
 *
 * ## `list` is built on `request`, never `send`
 *
 * `GET /replacements` is cursor-paginated — `{ data, meta: { next_cursor,
 * per_page, total } }`. `send()` resolves to `request()`'s `.data` alone and
 * throws `meta` away, so a caller built on it silently loses pagination.
 *
 * ## `mine` serialises as `1`/`0`, never `true`/`false`
 *
 * Laravel's `boolean` rule accepts `true`/`false`/`1`/`0`/`'1'`/`'0'` — the
 * STRING `'true'` (`String(true)`) is rejected. `queryString()` below
 * carries the identical fix the sibling modules document; that defect was
 * live in four modules at once before M-12 caught it.
 *
 * ## `status` DEFAULTS TO `requested` SERVER-SIDE, and `all` is not a state
 *
 * A caller that wants one case's whole replacement history — the
 * case-detail panel — must pass `status: 'all'` EXPLICITLY. M-09's and
 * M-10's identical panels both shipped or nearly shipped taking a default
 * that excluded every new row, and the legacy makes the same mistake in the
 * opposite direction: its Active list filters on a status string its own
 * create form has never written.
 *
 * ## `can` — what THIS CALLER may do to THIS row
 *
 * `ReplacementResource` publishes `can.dispense`/`can.reject`, computed
 * server-side from BOTH halves the server actually enforces: the
 * `replacement.fulfil` permission AND the terminal-state rule
 * (`ReplacementTransition`'s guard). Never re-derive either here from
 * `permissions` + `status`. M-11 shipped a resource with no capability
 * field and every decider got an Answer button that 403'd.
 *
 * Hiding a control is not a guard: `dispense`/`reject` still answer `409`
 * on a terminal row and `422` on an unavailable device, and this module
 * predicts neither.
 */

/** The three states `ReplacementResource.status` can carry (spec R-3). `dispensed` and `rejected` are both terminal. */
export type ReplacementStatus = 'requested' | 'dispensed' | 'rejected';

/** What `GET /replacements` filters `status` on — the three states plus `all` (`ReplacementIndexRequest::rules()`'s own `Rule::in`). */
export type ReplacementStatusFilter = ReplacementStatus | 'all';

/** One replacement covers exactly one device kind (spec R-2). */
export type ReplacementDeviceType = 'sensor' | 'reader';

/** The case a replacement belongs to — always eager-loaded server-side, never a second request from here. */
export interface ReplacementCaseRef {
  uuid: string;
  case_number: string;
  mobile: string | null;
}

/**
 * A registered device. The SAME three fields whether it is a sensor or a
 * reader, which is what lets one component render either.
 *
 * `old_device` is null when the typed serial matched nothing (spec R-5) —
 * `old_serial_number` is published beside it either way, so the screen
 * shows a link when it resolved and plain text when it did not.
 * `new_device` is null until the replacement is dispensed, AND stays null
 * afterwards whenever the dispensed serial matched no registered device —
 * `new_serial_number` is the value to render, exactly as `old_serial_number`
 * is on the other side.
 */
export interface ReplacementDeviceRef {
  uuid: string;
  serial_number: string;
  status: string;
}

export interface ReplacementDispensaryRef {
  uuid: string;
  name_en: string | null;
}

export interface ReplacementActorRef {
  uuid: string;
  full_name: string | null;
}

/**
 * What THIS CALLER may do to THIS row — `ReplacementResource`'s `can` key.
 * Both flags already fold the permission AND the terminal-state rule, so a
 * caller renders the control on `can.dispense` alone and never on
 * `permissions.includes('replacement.fulfil') && status === 'requested'`.
 */
export interface ReplacementAbilities {
  dispense: boolean;
  reject: boolean;
}

/**
 * One replacement, exactly as `GET /replacements`, `GET /replacements/
 * {replacement}`, `POST /replacements` and both action routes render it —
 * the SAME shape everywhere.
 */
export interface ReplacementRow {
  uuid: string;
  status: ReplacementStatus;
  device_type: ReplacementDeviceType;
  case: ReplacementCaseRef;
  perception_code: string | null;
  assessment_code: string | null;
  libre_type: string | null;
  /** The serial as TYPED, published whether or not it resolved to a device. */
  old_serial_number: string | null;
  old_device: ReplacementDeviceRef | null;
  /**
   * The serial DISPENSED, as typed — the counterpart of
   * `old_serial_number`, and the only record of it for a replacement whose
   * device was never in the registry. Every one migrated from the legacy
   * system is in that position, so reading `new_device` alone shows a
   * dispensed row naming nothing.
   */
  new_serial_number: string | null;
  new_device: ReplacementDeviceRef | null;
  reason: string | null;
  notes: string | null;
  requested_at: string | null;
  fulfilling_dispensary: ReplacementDispensaryRef | null;
  dispensed_at: string | null;
  dispensed_by: ReplacementActorRef | null;
  rejected_at: string | null;
  rejected_by: ReplacementActorRef | null;
  rejection_reason: string | null;
  created_at: string | null;
  can: ReplacementAbilities;
}

/**
 * Every filter `GET /replacements` accepts (`ReplacementIndexRequest::rules()`).
 * All optional — but `status` defaults to `requested` SERVER-SIDE, not to
 * `all`; see this module's own docblock.
 */
export interface ReplacementQuery {
  status?: ReplacementStatusFilter;
  case_number?: string;
  device_type?: ReplacementDeviceType;
  /** Narrows to replacements the caller RAISED. A server predicate, never a client-side narrowing of the fetched page. */
  mine?: boolean;
  cursor?: string;
  per_page?: number;
}

/** One page of the queue — cursor-paginated. */
export interface ReplacementPage {
  data: readonly ReplacementRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /** How many replacements match the selected filters, not how many are on this page. */
    total: number;
  };
}

/**
 * `POST /replacements`'s body (`StoreReplacementRequest`). `case_number`
 * must match an existing case — an unmatched one is a `422` naming the
 * field, never a row with a dangling string in it (spec R-1).
 *
 * A patient needing BOTH a reader and a sensor is two calls, not one field
 * carrying both (spec R-2).
 */
export interface StoreReplacementInput {
  case_number: string;
  device_type: ReplacementDeviceType;
  perception_code?: string | null;
  assessment_code?: string | null;
  libre_type?: string | null;
  old_serial_number?: string | null;
  reason?: string | null;
  notes?: string | null;
}

/**
 * `POST .../dispense`'s body (`DispenseReplacementRequest`). EXACTLY ONE of
 * the two device uuids, matching the replacement's own `device_type` — a
 * REGISTERED device, never a typed serial, because a free-text serial here
 * would recreate the legacy exactly, with no record of what the patient
 * actually received.
 */
export interface DispenseReplacementInput {
  new_sensor_uuid?: string | null;
  new_reader_uuid?: string | null;
  fulfilling_dispensary_uuid: string;
}

/** `POST .../reject`'s body (`RejectReplacementRequest`) — the reason is required and must survive a trim (spec R-3). */
export interface RejectReplacementInput {
  rejection_reason: string;
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper the sibling API modules each redeclare locally for
 * the same reason (none is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | boolean | undefined][]) {
    if (value === undefined) continue;

    // A BOOLEAN GOES OVER THE WIRE AS `1`/`0`, NEVER `true`/`false` — see
    // this module's own docblock for why `mine=true` would 422.
    search.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

const { request } = apiInternals;

export const replacementsApi = {
  /**
   * DELIBERATELY NOT `send<ReplacementPage>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: ReplacementQuery = {}): Promise<ReplacementPage> => {
    const { data, meta } = await request<readonly ReplacementRow[]>(`/replacements${queryString(query)}`);
    return { data, meta: meta as ReplacementPage['meta'] };
  },

  get: (uuid: string) => send<ReplacementRow>(`/replacements/${uuid}`),

  /** `422` naming `case_number` when it matches no case (spec R-1). */
  create: (input: StoreReplacementInput) =>
    send<ReplacementRow>('/replacements', { method: 'POST', body: input }),

  /** `409` on a terminal row; `422` when the device is the wrong kind, not `in_stock`, or on another dispensary's shelf. Neither is predicted here. */
  dispense: (uuid: string, input: DispenseReplacementInput) =>
    send<ReplacementRow>(`/replacements/${uuid}/dispense`, { method: 'POST', body: input }),

  /** `409` on a terminal row; `422` naming `rejection_reason` when it is blank. */
  reject: (uuid: string, input: RejectReplacementInput) =>
    send<ReplacementRow>(`/replacements/${uuid}/reject`, { method: 'POST', body: input }),
};
